import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { getProfessionalBySlug } from './professionalData';
import '../App.css';

const LOGO_FOLDER = encodeURIComponent('logos, pe');

const getLogoSrc = (logo) =>
  logo ? `/Projects/${LOGO_FOLDER}/${encodeURIComponent(logo)}` : null;

const getCompanyName = (companyAndRole = '') =>
  companyAndRole.split('\n')[0].split('—')[0].trim();

const WorksProfessionalDetailPage = ({ nightMode, setNightMode }) => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const entry = getProfessionalBySlug(slug);

  return (
    <div className={`works-project-detail ${nightMode ? 'night' : ''}`}>
      <div className="works-nav">
        <motion.button
          className="works-back"
          onClick={() => navigate('/works/professional')}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          whileHover={{ x: -6 }}
        >
          <ArrowLeft size={18} />
          <span>Back</span>
        </motion.button>

        <motion.button
          className="works-theme"
          onClick={() => setNightMode(!nightMode)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {nightMode ? '☀' : '🌙'}
        </motion.button>
      </div>

      <div className="project-detail-hero">
        <motion.span
          className="project-detail-eyebrow"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Professional Experience
        </motion.span>
        {entry?.logo ? (
          <motion.div
            className="professional-detail-logo"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.05 }}
          >
            <img
              src={getLogoSrc(entry.logo)}
              alt={`${getCompanyName(entry.companyAndRole)} logo`}
            />
          </motion.div>
        ) : null}
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          {entry
            ? entry.companyAndRole?.split('\n').map((line) => <span key={line}>{line}</span>)
            : 'Coming Soon'}
        </motion.h1>
        <motion.p
          className="project-detail-subtitle"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          {entry?.timeline || 'This collaboration will be documented shortly.'}
        </motion.p>
        {entry && (
          <motion.p
            className="project-detail-summary"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.16 }}
          >
            {entry.description.split('\n').find((para) => para.trim().length > 0)}
          </motion.p>
        )}
      </div>

      {entry ? (
        <>
          <div className="project-detail-body">
            <div className="project-detail-card">
              <span className="project-detail-label">Timeline</span>
              <span className="project-detail-value">{entry.timeline}</span>
            </div>
            <div className="project-detail-card project-detail-cta">
              <span className="project-detail-label">Back To Overview</span>
              <button onClick={() => navigate('/works/professional')}>Browse All</button>
            </div>
          </div>

          <section className="professional-detail-content">
            <div className="professional-detail-copy">
              {entry.description
                .split('\n')
                .filter((para) => para.trim().length > 0)
                .map((para) => (
                  <p key={para}>{para}</p>
                ))}
            </div>
            <div className="experience-detail-grid detail-view">
              <div className="experience-detail-card" style={{ '--experience-accent': entry.accent }}>
                <h3>Key Achievements</h3>
                <ul>
                  {entry.highlights.map((highlight) => (
                    <li key={highlight}>{highlight}</li>
                  ))}
                </ul>
              </div>
              {entry.skillsGained?.length ? (
                <div className="experience-detail-card" style={{ '--experience-accent': entry.accent }}>
                  <h3>Skills Gained</h3>
                  <ul>
                    {entry.skillsGained.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {entry.softSkills?.length ? (
                <div className="experience-detail-card" style={{ '--experience-accent': entry.accent }}>
                  <h3>Soft Skills</h3>
                  <ul>
                    {entry.softSkills.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {entry.tools?.length ? (
                <div className="experience-detail-card" style={{ '--experience-accent': entry.accent }}>
                  <h3>Tools & Platforms</h3>
                  <ul>
                    {entry.tools.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </section>
        </>
      ) : (
        <div className="project-detail-empty">
          <p>Details for this experience are coming soon.</p>
        </div>
      )}

      <Navigation activeSection="work" onNavigate={() => {}} nightMode={nightMode} />
    </div>
  );
};

export default WorksProfessionalDetailPage;
